import {
  activeBirthEvents,
  apgarTotal,
  cumulativeBloodLoss,
  gbsProphylaxisAdequate,
  instant,
  number,
  shockIndex,
  weightChangePct,
  type BirthEvent,
} from './birthCalculations';
export const birthRuleParameters: Record<string, string[]> = {
  'R-IP-01': ['low', 'high'],
  'R-IP-02': ['warn', 'critical', 'recheck_minutes'],
  'R-IP-03': ['warn_systolic', 'warn_diastolic', 'critical_systolic', 'critical_diastolic'],
  'R-IP-04': ['warn', 'critical'],
  'R-IP-05': ['warn_minutes', 'critical_minutes'],
  'R-IP-06': [],
  'R-IP-07': ['ga_days'],
  'R-IP-08': ['warn_minutes', 'critical_minutes'],
  'R-IP-09': ['minutes'],
  'R-IP-10': ['nullip_minutes', 'multip_minutes'],
  'R-IP-11': ['minimum_hours'],
  'R-PP-01': ['warn', 'critical'],
  'R-PP-02': ['warn', 'critical', 'pair_window_minutes'],
  'R-PP-03': [],
  'R-PP-04': ['active_minutes', 'physiologic_minutes'],
  'R-PP-05': [],
  'R-PP-06': ['minutes'],
  'R-NB-01': ['critical_low', 'low', 'high'],
  'R-NB-02': ['low', 'high'],
  'R-NB-03': ['low', 'high'],
  'R-NB-04': ['minute', 'low'],
  'R-NB-05': ['after_minutes', 'low'],
  'R-NB-06': ['warn', 'critical'],
  'R-NB-07': [],
  'R-NB-08': ['before_minutes'],
  'R-NB-09': ['urine_minutes', 'stool_minutes'],
};
export const birthRuleTerms: Record<string, string[]> = {
  'R-IP-06': ['meconium'],
  'R-IP-11': ['drugs'],
  'R-PP-03': ['boggy'],
  'R-PP-05': ['lacerations'],
  'R-NB-02': ['distress'],
  'R-NB-03': ['axillary'],
  'R-NB-05': ['cyanosis'],
  'R-NB-07': ['screen', 'failed'],
  'R-NB-08': ['jaundice'],
};
export interface BirthRule {
  id: string;
  enabled: boolean;
  citation: string;
  message: string;
  params: Record<string, number>;
  terms?: Record<string, string[]>;
}
export interface BirthTimerProtocol {
  once?: boolean;
  observation_match?: Record<string, string | number | boolean>;
  start_after_minutes?: number;
  id: string;
  enabled: boolean;
  citation: string;
  label: string;
  subject: 'MOTHER' | 'FETUS' | 'NEWBORN';
  start: string;
  stop: string[];
  observations: string[];
  interval_minutes: number;
  warning_fraction: number;
  max_duration_minutes: number | null;
}
export interface BirthRuleConfiguration {
  clinically_reviewed: boolean;
  rules: BirthRule[];
  timers: BirthTimerProtocol[];
}
export interface BirthRuleContext {
  episode_id: string;
  mother_id: string;
  fetus_ids: string[];
  newborns: {
    subject_id: string;
    fetus_id: string;
    birth_at: string;
    birth_event_id: string;
    birth_weight_g?: number;
  }[];
  now: string;
  stage: 'INTRAPARTUM' | 'PP_MATERNAL' | 'NEWBORN';
  labor_complete?: boolean;
  ga_days?: number;
  parity?: number;
  gbs_status?: 'POSITIVE' | 'NEGATIVE' | 'UNKNOWN';
  third_stage_management?: 'ACTIVE' | 'PHYSIOLOGIC';
}
export interface BirthAlert {
  id: string;
  rule_id: string;
  subject_id: string;
  severity: 'info' | 'warn' | 'critical';
  message: string;
  value: unknown;
  source_event_ids: string[];
  citation: string;
}
export interface BirthTimer {
  id: string;
  protocol_id: string;
  subject_id: string;
  label: string;
  citation: string;
  anchor_event_id: string;
  due_at_ms: number;
  elapsed_minutes: number;
  state: 'scheduled' | 'due_soon' | 'overdue';
}
const record = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const keys = (v: Record<string, unknown>, allowed: string[]) =>
  Object.keys(v).sort().join('|') === [...allowed].sort().join('|');
export function validateBirthConfiguration(input: unknown): string[] {
  const errors: string[] = [];
  if (!record(input) || Object.keys(input).some((k) => !['clinically_reviewed', 'rules', 'timers'].includes(k)))
    return ['Invalid birth configuration.'];
  if (typeof input.clinically_reviewed !== 'boolean' || !Array.isArray(input.rules) || !Array.isArray(input.timers))
    return ['Review status, rules and timers required.'];
  if (input.rules.length > Object.keys(birthRuleParameters).length || input.timers.length > 50)
    return ['Too many rules or timers.'];
  const seen = new Set<string>();
  for (const r of input.rules) {
    if (
      !record(r) ||
      Object.keys(r).some((k) => !['id', 'enabled', 'citation', 'message', 'params', 'terms'].includes(k))
    ) {
      errors.push('Unsupported rule fields.');
      continue;
    }
    const id = String(r.id);
    if (!Object.hasOwn(birthRuleParameters, id) || seen.has(id)) {
      errors.push('Unknown or duplicate rule.');
      continue;
    }
    seen.add(id);
    const p = r.params ?? {},
      terms = r.terms ?? {};
    if (
      !record(p) ||
      !keys(p, birthRuleParameters[id]) ||
      Object.values(p).some((v) => !number(v) || v < 0 || v > 100000)
    )
      errors.push(id + ': explicit bounded numeric parameters required.');
    else {
      for (const [a, b] of [
        ['low', 'high'],
        ['warn', 'critical'],
        ['warn_minutes', 'critical_minutes'],
        ['warn_systolic', 'critical_systolic'],
        ['warn_diastolic', 'critical_diastolic'],
        ['critical_low', 'low'],
      ])
        if (number(p[a]) && number(p[b]) && p[a] > p[b]) errors.push(id + ': threshold order invalid.');
      if (Object.entries(p).some(([k, v]) => (k.includes('minutes') || k === 'minimum_hours') && (v as number) <= 0))
        errors.push(id + ': positive duration required.');
    }
    if (
      !record(terms) ||
      !keys(terms, birthRuleTerms[id] ?? []) ||
      Object.values(terms).some(
        (v) =>
          !Array.isArray(v) ||
          !v.length ||
          v.length > 50 ||
          v.some((s) => typeof s !== 'string' || !s.trim() || s.length > 100),
      )
    )
      errors.push(id + ': explicit supported terms required.');
    if (
      typeof r.enabled !== 'boolean' ||
      ['citation', 'message'].some(
        (k) => typeof r[k] !== 'string' || !(r[k] as string).trim() || (r[k] as string).length > 2000,
      )
    )
      errors.push(id + ': enabled, citation and message required.');
    if (r.enabled && !input.clinically_reviewed) errors.push(id + ': clinical review required.');
  }
  seen.clear();
  for (const t of input.timers) {
    if (
      !record(t) ||
      !keys(
        Object.fromEntries(
          Object.entries(t).filter(([k]) => !['once', 'observation_match', 'start_after_minutes'].includes(k)),
        ),
        [
          'id',
          'enabled',
          'citation',
          'label',
          'subject',
          'start',
          'stop',
          'observations',
          'interval_minutes',
          'warning_fraction',
          'max_duration_minutes',
        ],
      )
    ) {
      errors.push('Invalid timer fields.');
      continue;
    }
    if (
      ('once' in t && typeof t.once !== 'boolean') ||
      ('observation_match' in t &&
        (!record(t.observation_match) ||
          Object.values(t.observation_match).some(
            (v) => !['string', 'number', 'boolean'].includes(typeof v) || (typeof v === 'number' && !number(v)),
          )))
    )
      errors.push('Invalid timer observation match or once flag.');
    if (
      !number(t.start_after_minutes ?? 0) ||
      (t.start_after_minutes as number) < 0 ||
      (t.start_after_minutes as number) > 10080
    )
      errors.push('Invalid timer start delay.');
    if (typeof t.id !== 'string' || !t.id.trim() || seen.has(t.id)) errors.push('Invalid or duplicate timer id.');
    seen.add(String(t.id));
    if (
      !['MOTHER', 'FETUS', 'NEWBORN'].includes(String(t.subject)) ||
      !['ADMITTED', 'ACTIVE_LABOR', 'SECOND_STAGE', 'PUSHING_START', 'ROM', 'BIRTH', 'PLACENTA'].includes(
        String(t.start),
      )
    )
      errors.push('Unsupported timer scope or start.');
    if (
      ['stop', 'observations'].some(
        (k) => !Array.isArray(t[k]) || (t[k] as unknown[]).some((x) => typeof x !== 'string' || !x.trim()),
      )
    )
      errors.push('Timer observations and stops must be event/milestone names.');
    if (
      !number(t.interval_minutes) ||
      t.interval_minutes <= 0 ||
      t.interval_minutes > 10080 ||
      !number(t.warning_fraction) ||
      t.warning_fraction <= 0 ||
      t.warning_fraction > 1 ||
      (t.max_duration_minutes !== null &&
        (!number(t.max_duration_minutes) || t.max_duration_minutes <= 0 || t.max_duration_minutes > 10080))
    )
      errors.push('Invalid timer interval/window.');
    if (
      typeof t.enabled !== 'boolean' ||
      ['citation', 'label'].some((k) => typeof t[k] !== 'string' || !(t[k] as string).trim()) ||
      (t.enabled && !input.clinically_reviewed)
    )
      errors.push('Timer requires explicit review, citation and label.');
  }
  return errors;
}
export function evaluateBirthRules(
  events: BirthEvent[],
  context: BirthRuleContext,
  config: BirthRuleConfiguration,
): { alerts: BirthAlert[]; timers: BirthTimer[]; errors: string[] } {
  const errors = validateBirthConfiguration(config);
  if (errors.length || !config.clinically_reviewed) return { alerts: [], timers: [], errors };
  const now = instant(context.now);
  if (now === null) return { alerts: [], timers: [], errors: ['Timezone-aware now required.'] };
  const all = activeBirthEvents(events, context.episode_id, context.now),
    mother = context.mother_id,
    fetalIds = context.fetus_ids ?? [],
    births = all.filter((e) => e.event_type === 'BIRTH' && fetalIds.includes(e.subject_id)),
    firstBirth = births[0],
    lastBirth = births.at(-1),
    babies: BirthRuleContext['newborns'] = [];
  for (const b of context.newborns ?? []) {
    if (
      births.some(
        (e) =>
          e.id === b.birth_event_id && e.subject_id === b.fetus_id && instant(e.occurred_at) === instant(b.birth_at),
      ) &&
      ![mother, ...fetalIds].includes(b.subject_id) &&
      !babies.some((x) => x.subject_id === b.subject_id)
    )
      babies.push(b);
  }
  const alerts: BirthAlert[] = [];
  const subset = (sid: string, post = false) =>
    all.filter(
      (e) =>
        e.subject_id === sid && (!post || (firstBirth && instant(e.occurred_at)! >= instant(firstBirth.occurred_at)!)),
    );
  const latest = (items: BirthEvent[], kind: string | undefined) =>
    [...items].reverse().find((e) => e.event_type === kind);
  const elapsed = (e: BirthEvent | undefined) => (e ? (now - instant(e.occurred_at)!) / 60000 : null);
  const matches = (v: unknown, terms: string[]) =>
    typeof v === 'string' && terms.map((s) => s.toLowerCase()).includes(v.toLowerCase());
  const emit = (
    r: BirthRule,
    sid: string,
    severity: BirthAlert['severity'] | null,
    value: unknown,
    sources: (BirthEvent | string | undefined)[],
  ) => {
    if (!severity) return;
    const ids = [
      ...new Set(sources.filter((e): e is BirthEvent | string => !!e).map((e) => (typeof e === 'string' ? e : e.id))),
    ];
    alerts.push({
      id: r.id + ':' + sid + ':' + ids.join(','),
      rule_id: r.id,
      subject_id: sid,
      severity,
      message: r.message,
      value,
      source_event_ids: ids,
      citation: r.citation,
    });
  };
  const maternal = subset(mother);
  for (const r of config.rules) {
    if (!r.enabled) continue;
    const key = r.id,
      p = r.params,
      terms = r.terms ?? {};
    const ip = context.stage === 'INTRAPARTUM' && !context.labor_complete;
    if (key.startsWith('R-IP') && key !== 'R-IP-11' && !ip) continue;
    if (key.startsWith('R-PP') && !firstBirth) continue;
    if (key === 'R-IP-01') {
      for (const sid of fetalIds) {
        if (births.some((e) => e.subject_id === sid)) continue;
        const e = latest(subset(sid), 'LABOR.FHR');
        if (e) {
          const v = e.payload;
          const abnormal =
            (number(v.low_bpm) && v.low_bpm < p.low) ||
            (number(v.high_bpm) && v.high_bpm > p.high) ||
            v.decels === true;
          emit(r, sid, abnormal ? 'warn' : null, v, [e]);
        }
      }
    } else if (['R-IP-02', 'R-IP-03', 'R-IP-04'].includes(key)) {
      const e = latest(
        maternal,
        ({ 'R-IP-02': 'VITALS.TEMP', 'R-IP-03': 'VITALS.BP', 'R-IP-04': 'VITALS.PULSE' } as Record<string, string>)[
          key
        ],
      );
      if (!e) continue;
      const v = e.payload;
      let severity: BirthAlert['severity'] | null = null;
      if (key === 'R-IP-03') {
        if (number(v.systolic) && number(v.diastolic))
          severity =
            v.systolic >= p.critical_systolic || v.diastolic >= p.critical_diastolic
              ? 'critical'
              : v.systolic >= p.warn_systolic || v.diastolic >= p.warn_diastolic
                ? 'warn'
                : null;
      } else {
        const n = v[key === 'R-IP-02' ? 'value_c' : 'rate'];
        if (number(n))
          severity =
            key === 'R-IP-02'
              ? n >= p.critical
                ? 'critical'
                : n >= p.warn
                  ? 'warn'
                  : null
              : n > p.critical
                ? 'critical'
                : n > p.warn
                  ? 'warn'
                  : null;
      }
      const sources = [e];
      if (key === 'R-IP-02' && severity === 'warn') {
        const previous = maternal
          .filter((x) => x.event_type === 'VITALS.TEMP' && instant(x.occurred_at)! < instant(e.occurred_at)!)
          .at(-1);
        if (
          previous &&
          number(previous.payload.value_c) &&
          previous.payload.value_c >= p.warn &&
          previous.payload.method === v.method &&
          (instant(e.occurred_at)! - instant(previous.occurred_at)!) / 60000 >= p.recheck_minutes
        ) {
          severity = 'critical';
          sources.push(previous);
        }
      }
      emit(r, mother, severity, v, sources);
    } else if (key === 'R-IP-05') {
      const e = maternal.find((e) => e.event_type === 'ROM'),
        n = elapsed(e);
      if (n !== null)
        emit(r, mother, n >= p.critical_minutes ? 'critical' : n >= p.warn_minutes ? 'warn' : null, n, [e]);
    } else if (key === 'R-IP-06') {
      const e = latest(maternal, 'ROM');
      if (e && matches(e.payload.fluid_color, terms.meconium)) emit(r, mother, 'warn', e.payload.fluid_color, [e]);
    } else if (key === 'R-IP-07') {
      const ga = context.ga_days,
        e = maternal.find(
          (e) => e.event_type === 'MILESTONE' && ['LABOR_START', 'ACTIVE_LABOR'].includes(String(e.payload.milestone)),
        );
      if (e && number(ga) && ga < p.ga_days) emit(r, mother, 'critical', ga, [e]);
    } else if (key === 'R-IP-08') {
      for (const sid of fetalIds) {
        if (births.some((e) => e.subject_id === sid)) continue;
        const e = latest(subset(sid), 'LABOR.FHR'),
          n = elapsed(e);
        if (n !== null)
          emit(r, sid, n >= p.critical_minutes ? 'critical' : n >= p.warn_minutes ? 'warn' : null, n, [e]);
      }
    } else if (key === 'R-IP-09') {
      const active = maternal.find((e) => e.event_type === 'MILESTONE' && e.payload.milestone === 'ACTIVE_LABOR'),
        exams = maternal.filter(
          (e) => e.event_type === 'LABOR.PROGRESS' && active && instant(e.occurred_at)! >= instant(active.occurred_at)!,
        );
      if (exams.length) {
        let change = exams[0];
        for (let i = 1; i < exams.length; i++)
          if (exams[i - 1].payload.dilation_cm !== exams[i].payload.dilation_cm) change = exams[i];
        if (elapsed(change)! >= p.minutes) emit(r, mother, 'info', elapsed(change), [change, exams.at(-1)]);
      }
    } else if (key === 'R-IP-10') {
      const e = maternal.find((e) => e.event_type === 'MILESTONE' && e.payload.milestone === 'SECOND_STAGE');
      if (
        e &&
        number(context.parity) &&
        context.parity >= 0 &&
        elapsed(e)! > p[context.parity === 0 ? 'nullip_minutes' : 'multip_minutes']
      )
        emit(r, mother, 'warn', elapsed(e), [e]);
    } else if (key === 'R-IP-11') {
      if (context.gbs_status === 'POSITIVE')
        for (const birth of births) {
          const calc = gbsProphylaxisAdequate(
            all,
            context.episode_id,
            mother,
            birth.occurred_at,
            terms.drugs,
            p.minimum_hours,
            true,
          );
          if (calc.value !== true)
            emit(r, birth.subject_id, 'warn', { duration_criterion_met: calc.value }, [
              birth,
              ...(calc.inputs_used as string[]),
            ]);
        }
    } else if (key === 'R-PP-01') {
      const calc = cumulativeBloodLoss(all, context.episode_id, mother, firstBirth.occurred_at, context.now),
        n = calc.value.total_ml,
        sign = latest(subset(mother, true), 'PP.BLOOD_LOSS');
      emit(
        r,
        mother,
        sign?.payload.hypovolemia_signs === true || (n !== null && n >= p.critical)
          ? 'critical'
          : n !== null && n >= p.warn
            ? 'warn'
            : null,
        calc.value,
        [firstBirth, ...(calc.inputs_used as string[]), sign],
      );
    } else if (key === 'R-PP-02') {
      const pulse = latest(subset(mother, true), 'VITALS.PULSE'),
        bp = latest(subset(mother, true), 'VITALS.BP');
      if (
        pulse &&
        bp &&
        Math.abs(instant(pulse.occurred_at)! - instant(bp.occurred_at)!) / 60000 <= p.pair_window_minutes
      ) {
        const n = shockIndex(pulse.payload.rate, bp.payload.systolic).value;
        if (n !== null) emit(r, mother, n >= p.critical ? 'critical' : n >= p.warn ? 'warn' : null, n, [pulse, bp]);
      }
    } else if (['R-PP-03', 'R-PP-05'].includes(key)) {
      const e = latest(subset(mother, true), key === 'R-PP-03' ? 'PP.FUNDUS' : 'PP.PERINEUM'),
        [field, term] = key === 'R-PP-03' ? ['tone', 'boggy'] : ['status', 'lacerations'];
      if (e && matches(e.payload[field], terms[term]))
        emit(r, mother, key === 'R-PP-03' ? 'warn' : 'critical', e.payload[field], [e]);
    } else if (key === 'R-PP-04') {
      const mode = context.third_stage_management;
      if (
        mode &&
        lastBirth &&
        !latest(subset(mother, true), 'PLACENTA') &&
        elapsed(lastBirth)! >= p[mode === 'ACTIVE' ? 'active_minutes' : 'physiologic_minutes']
      )
        emit(r, mother, 'warn', elapsed(lastBirth), [lastBirth]);
    } else if (key === 'R-PP-06') {
      const voided = subset(mother, true).some(
        (e) => e.event_type === 'IO.OUTPUT' && String(e.payload.type ?? '').toUpperCase() === 'URINE',
      );
      if (!voided && elapsed(firstBirth)! >= p.minutes)
        emit(r, mother, 'warn', { minutes_without_documented_void: elapsed(firstBirth) }, [firstBirth]);
    } else if (key.startsWith('R-NB'))
      for (const baby of babies) {
        const sid = baby.subject_id,
          start = instant(baby.birth_at)!,
          items = subset(sid).filter((e) => instant(e.occurred_at)! >= start),
          age = (now - start) / 60000;
        const kind = (
          {
            'R-NB-01': 'VITALS.PULSE',
            'R-NB-02': 'VITALS.RR',
            'R-NB-03': 'VITALS.TEMP',
            'R-NB-04': 'APGAR',
            'R-NB-05': 'VITALS.SPO2',
            'R-NB-06': 'VITALS.WEIGHT',
            'R-NB-07': 'NB.SCREEN',
            'R-NB-08': 'NB.COLOR',
          } as Record<string, string>
        )[key];
        let e = latest(items, kind),
          sev: BirthAlert['severity'] | null = null,
          value: unknown = null;
        const sources: (BirthEvent | string | undefined)[] = [baby.birth_event_id];
        if (key === 'R-NB-01' && e && number(e.payload.rate)) {
          const n = e.payload.rate;
          value = n;
          sev = n < p.critical_low ? 'critical' : n < p.low || n > p.high ? 'warn' : null;
        } else if (key === 'R-NB-02' && e) {
          const n = e.payload.rate;
          value = n;
          sev =
            (number(n) && (n < p.low || n > p.high)) ||
            e.payload.respiratory_distress === true ||
            matches(e.payload.quality, terms.distress)
              ? 'warn'
              : null;
        } else if (key === 'R-NB-03' && e && matches(e.payload.method, terms.axillary)) {
          const n = e.payload.value_c;
          value = n;
          sev = number(n) && (n < p.low || n > p.high) ? 'warn' : null;
        } else if (key === 'R-NB-04') {
          e = [...items].reverse().find((e) => e.event_type === 'APGAR' && e.payload.minute === p.minute);
          const n = e ? apgarTotal(e.payload).value : null;
          value = n;
          sev = n !== null && n < p.low ? 'warn' : null;
        } else if (key === 'R-NB-05') {
          const color = latest(items, 'NB.COLOR'),
            relevant = [e, color].filter(
              (v): v is BirthEvent => !!v && (instant(v.occurred_at)! - start) / 60000 > p.after_minutes,
            );
          sev = relevant.some(
            (v) =>
              (v.event_type === 'VITALS.SPO2' && number(v.payload.value_pct) && v.payload.value_pct < p.low) ||
              (v.event_type === 'NB.COLOR' && matches(v.payload.value, terms.cyanosis)),
          )
            ? 'critical'
            : null;
          sources.push(...relevant);
        } else if (key === 'R-NB-06' && e) {
          const n = weightChangePct(e.payload.grams, baby.birth_weight_g).value;
          value = n;
          sev = n !== null && -n >= p.critical ? 'critical' : n !== null && -n >= p.warn ? 'warn' : null;
        } else if (key === 'R-NB-07') {
          e = [...items].reverse().find((v) => v.event_type === 'NB.SCREEN' && matches(v.payload.type, terms.screen));
          sev = e && matches(e.payload.result, terms.failed) ? 'critical' : null;
        } else if (key === 'R-NB-08' && e) {
          const n = (instant(e.occurred_at)! - start) / 60000;
          value = n;
          sev =
            n < p.before_minutes && (e.payload.jaundice === true || matches(e.payload.value, terms.jaundice))
              ? 'critical'
              : null;
        } else if (key === 'R-NB-09') {
          const missing = [
            ['URINE', p.urine_minutes],
            ['STOOL', p.stool_minutes],
          ] as [string, number][];
          const list = missing
            .filter(
              ([kind, threshold]) =>
                age >= threshold &&
                !items.some((e) => e.event_type === 'IO.OUTPUT' && String(e.payload.type ?? '').toUpperCase() === kind),
            )
            .map(([kind]) => kind);
          value = { not_documented: list, age_minutes: age };
          sev = list.length ? 'warn' : null;
        }
        emit(r, sid, sev, value, [...sources, e]);
      }
  }
  return { alerts, timers: birthTimers(all, context, config, babies), errors: [] };
}
export function birthTimers(
  events: BirthEvent[],
  context: BirthRuleContext,
  config: BirthRuleConfiguration,
  babies: BirthRuleContext['newborns'],
): BirthTimer[] {
  const timers: BirthTimer[] = [],
    now = instant(context.now)!;
  for (const p of config.timers) {
    if (!p.enabled) continue;
    const subjects =
      p.subject === 'MOTHER'
        ? [context.mother_id]
        : p.subject === 'FETUS'
          ? context.fetus_ids
          : babies.map((b) => b.subject_id);
    for (const sid of subjects) {
      const linked = babies.find((b) => b.subject_id === sid),
        own = events.filter((e) => e.subject_id === sid);
      let scope = events.filter(
        (e) => e.subject_id === context.mother_id || e.subject_id === sid || (linked && e.id === linked.birth_event_id),
      );
      if (p.subject === 'MOTHER')
        scope = scope.concat(
          events.filter((e) => context.fetus_ids.includes(e.subject_id) && e.event_type === 'BIRTH'),
        );
      const matches = (e: BirthEvent, labels: string[]) =>
        labels.includes(e.event_type) || (e.event_type === 'MILESTONE' && labels.includes(String(e.payload.milestone)));
      const starts = scope.filter((e) => matches(e, [p.start]));
      if (!starts.length) continue;
      const start = starts.at(-1)!;
      if (scope.some((e) => matches(e, p.stop) && instant(e.occurred_at)! >= instant(start.occurred_at)!)) continue;
      if (p.max_duration_minutes !== null && (now - instant(start.occurred_at)!) / 60000 > p.max_duration_minutes)
        continue;
      const startTime = instant(start.occurred_at)! + (p.start_after_minutes ?? 0) * 60000;
      if (now < startTime) continue;
      const observations = own.filter(
        (e) =>
          p.observations.includes(e.event_type) &&
          instant(e.occurred_at)! >= startTime &&
          Object.entries(p.observation_match ?? {}).every(([k, v]) => e.payload[k] === v),
      );
      const latestByType = new Map(observations.map((e) => [e.event_type, e]));
      const complete = !!p.observations.length && p.observations.every((k) => latestByType.has(k));
      if (p.once && complete) continue;
      const anchor = complete
        ? [...latestByType.values()].sort((a, b) => instant(a.occurred_at)! - instant(b.occurred_at)!)[0]
        : start;
      const anchorTime = complete ? instant(anchor.occurred_at)! : startTime;
      const elapsed = (now - anchorTime) / 60000;
      timers.push({
        id: p.id + ':' + sid + ':' + anchor.id,
        protocol_id: p.id,
        subject_id: sid,
        label: p.label,
        citation: p.citation,
        anchor_event_id: anchor.id,
        due_at_ms: anchorTime + p.interval_minutes * 60000,
        elapsed_minutes: elapsed,
        state:
          elapsed >= p.interval_minutes
            ? 'overdue'
            : elapsed >= p.interval_minutes * p.warning_fraction
              ? 'due_soon'
              : 'scheduled',
      });
    }
  }
  for (const r of config.rules) {
    if (r.id !== 'R-IP-02' || !r.enabled || context.stage !== 'INTRAPARTUM' || context.labor_complete) continue;
    const temps = events.filter((e) => e.subject_id === context.mother_id && e.event_type === 'VITALS.TEMP'),
      e = temps.at(-1);
    if (e && number(e.payload.value_c) && e.payload.value_c >= r.params.warn) {
      const due = instant(e.occurred_at)! + r.params.recheck_minutes * 60000;
      timers.push({
        id: 'R-IP-02:' + e.id,
        protocol_id: 'R-IP-02',
        subject_id: e.subject_id,
        label: 'Temperature recheck',
        citation: r.citation,
        anchor_event_id: e.id,
        due_at_ms: due,
        elapsed_minutes: (now - instant(e.occurred_at)!) / 60000,
        state: now >= due ? 'overdue' : 'scheduled',
      });
    }
  }
  return timers;
}
